import { Layers, BarChart3, Users, CheckSquare, Zap, Activity } from 'lucide-react';
import KeywordLandingPage, {
  type ProblemCard,
  type FeatureCard,
} from '../components/KeywordLandingPage';

const problemCards: ProblemCard[] = [
  {
    title: 'Strategic context gets lost',
    description:
      'A project list can show what is happening without showing the objectives and strategies behind it.',
    icon: Layers,
    iconBgClass: 'bg-red-100',
    iconColorClass: 'text-red-600',
  },
  {
    title: 'The bigger picture takes effort',
    description:
      'Separate work views make it harder to move between an individual task and the company plan.',
    icon: BarChart3,
    iconBgClass: 'bg-orange-100',
    iconColorClass: 'text-orange-600',
  },
  {
    title: 'Next actions are difficult to follow',
    description:
      'A task needs clear responsibility, priority, and a deadline to become a practical next step.',
    icon: Users,
    iconBgClass: 'bg-yellow-100',
    iconColorClass: 'text-yellow-600',
  },
];

const features: FeatureCard[] = [
  {
    title: 'A connected strategic hierarchy',
    description: 'Explore objectives, strategies, projects, tasks, and subtasks together.',
    icon: Layers,
    gradientFrom: 'from-blue-50',
    gradientTo: 'to-blue-100',
    iconColorClass: 'text-blue-600',
  },
  {
    title: 'Execution Hub',
    description: 'Manage assigned work, dates, priorities, and status in one delivery workspace.',
    icon: CheckSquare,
    gradientFrom: 'from-purple-50',
    gradientTo: 'to-purple-100',
    iconColorClass: 'text-purple-600',
  },
  {
    title: 'Different views of the work',
    description:
      'Use hierarchy, list, board, map, and timeline views to answer different planning questions.',
    icon: BarChart3,
    gradientFrom: 'from-green-50',
    gradientTo: 'to-green-100',
    iconColorClass: 'text-green-600',
  },
  {
    title: 'Organisation and access',
    description: 'Map divisions, teams, and positions, invite people, and manage custom roles.',
    icon: Users,
    gradientFrom: 'from-orange-50',
    gradientTo: 'to-orange-100',
    iconColorClass: 'text-orange-600',
  },
  {
    title: 'AI delivery drafts',
    description: 'Use strategy and project context to draft projects, tasks, and subtasks.',
    icon: Zap,
    gradientFrom: 'from-red-50',
    gradientTo: 'to-red-100',
    iconColorClass: 'text-red-600',
  },
  {
    title: 'Visual strategy reports',
    description: 'Arrange your plan in custom reports and export a PDF for discussion.',
    icon: Activity,
    gradientFrom: 'from-indigo-50',
    gradientTo: 'to-indigo-100',
    iconColorClass: 'text-indigo-600',
  },
];

export default function StrategicExecutionPlatformPage() {
  return (
    <KeywordLandingPage
      headline="Bring strategy and delivery into the same view."
      subheadline="See the path from company objectives to individual tasks. Organise the work with owners, deadlines, and progress views in StratPlan’s Execution Hub."
      bullets={[
        'Objectives, strategies, projects, and tasks linked',
        'An Execution Hub for the next actions',
        'Hierarchy, board, list, map, and timeline views',
        'Organisation structure and assigned responsibility',
      ]}
      badgeText="Strategic Execution Platform"
      problemTitle="Execution needs the context behind the work."
      problemSubtitle="When the strategy and delivery plan are separate, it takes more effort to see the priority, the owner, and the current progress together."
      problemCards={problemCards}
      solutionTitle="Move from the strategic picture to the next task."
      solutionSubtitle="StratPlan connects the planning hierarchy to an Execution Hub, so you can organise delivery while keeping the purpose of the work visible."
      solutionSteps={[
        {
          title: 'Connect the hierarchy',
          description:
            'Build the relationship from objective to strategy, project, task, and subtask.',
        },
        {
          title: 'Organise delivery',
          description:
            'Assign work, set priorities and dates, and choose the view that helps you manage the next actions.',
        },
        {
          title: 'Follow and explain progress',
          description:
            'Update work status, explore progress in context, and bring your plan into a visual report.',
        },
      ]}
      featuresTitle="A shared structure for strategy and delivery"
      features={features}
      finalCtaTitle="Connect the plan to the work that follows."
      finalCtaSubtitle="Create your account and start organising strategic delivery in a connected workspace."
      finalCtaButtonText="Start my strategic plan"
      analyticsCategory="strategic_execution_platform_landing"
      breadcrumbLabel="Strategic Execution Platform"
    />
  );
}
